import {
  AccountCircle,
  ChevronLeft,
  ChevronRight,
  HelpOutline,
  Leaderboard,
  Login,
  NoteAlt,
  RateReview,
  RequestPage,
  Search,
} from '@mui/icons-material';
import {
  Badge,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { styled as MuiStyled } from '@mui/material/styles';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import kandirLogo from '../../assets/images/kandir_logo_blue.svg';
import { UserContext } from '../contexts/UserContext';
import { useResponsive } from '@shared/hooks/useResponsive';
import { layout } from '@shared/theme/utils';
import { useNetworkQuestionsContext } from '../../scenes/network-questions/NetworkQuestionsContext';

interface SideDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const drawerWidthExpanded = 280;
const drawerWidthCollapsed = 72;

// Routes where the side drawer should be completely hidden
const HIDDEN_DRAWER_ROUTES = [
  '/',
  '/sign-in',
  '/sign-up',
  '/verification',
  '/onboarding',
];

export function shouldHideDrawer(pathname: string): boolean {
  return HIDDEN_DRAWER_ROUTES.some(
    (route) =>
      pathname === route ||
      pathname.startsWith(`${route}/`) ||
      pathname.startsWith(`${route}?`),
  );
}

export const Main = MuiStyled('main', {
  shouldForwardProp: (prop) =>
    prop !== 'open' && prop !== 'isMobile' && prop !== 'hideDrawer',
})<{
  open?: boolean;
  isMobile?: boolean;
  hideDrawer?: boolean;
}>(({ theme, open, isMobile, hideDrawer }) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  '--sidebar-width': hideDrawer
    ? '0px'
    : !isMobile
      ? open
        ? `${drawerWidthExpanded}px`
        : `${drawerWidthCollapsed}px`
      : open
        ? `${drawerWidthExpanded}px`
        : '0px',
  marginLeft: hideDrawer
    ? '0px'
    : !isMobile
      ? open
        ? `${drawerWidthExpanded}px`
        : `${drawerWidthCollapsed}px`
      : '0px',
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  badgeCount?: number;
}

interface DrawerNavItemProps {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  testId: string;
}

const DrawerNavItem = ({
  item,
  active,
  collapsed,
  testId,
}: DrawerNavItemProps): React.ReactElement => {
  const { text, icon, path, badgeCount = 0 } = item;

  const iconNode
    = badgeCount > 0 && collapsed
      ? (
          <Badge
            badgeContent={badgeCount}
            max={99}
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            className="side-drawer__badge"
          >
            {icon}
          </Badge>
        )
      : icon;

  const buttonClass = collapsed
    ? `side-drawer__item${active ? ' side-drawer__item--active' : ''} side-drawer__item--collapsed`
    : `side-drawer__item${active ? ' side-drawer__item--active' : ''}`;

  const iconClass = collapsed
    ? 'side-drawer__item-icon side-drawer__item-icon--collapsed'
    : 'side-drawer__item-icon';

  return (
    <ListItem disablePadding>
      <Tooltip title={collapsed ? text : ''} placement="right" arrow>
        <Link to={path} className="side-drawer__item-link">
          <ListItemButton
            className={buttonClass}
            data-testid={testId}
            // Wrap MUI's ripple/disable etc. via className; layout
            // alignment driven by --collapsed modifier instead of inline.
          >
            <ListItemIcon className={iconClass}>{iconNode}</ListItemIcon>
            {!collapsed && (
              <>
                <ListItemText
                  className="side-drawer__item-text"
                  primary={text}
                />
                {badgeCount > 0 && (
                  <Badge
                    badgeContent={badgeCount}
                    max={99}
                    className="side-drawer__badge side-drawer__badge--inline"
                  />
                )}
              </>
            )}
          </ListItemButton>
        </Link>
      </Tooltip>
    </ListItem>
  );
};

const SideDrawer = ({ isOpen, setIsOpen }: SideDrawerProps): React.ReactElement | null => {
  const { user } = useContext(UserContext);
  const { questions: networkQuestions } = useNetworkQuestionsContext();
  const networkQuestionsCount = networkQuestions.length;
  const { isMobile } = useResponsive();
  const location = useLocation();
  const hasFired = useRef(false);

  useEffect(() => {
    if (!hasFired.current) {
      hasFired.current = true;
    }
  }, []);

  const isActive = (path: string): boolean => {
    return (
      location.pathname === path
      || (path === '/contribute' && location.pathname.startsWith('/contribute'))
      || (path === '/my-reviews' && location.pathname.startsWith('/my-reviews'))
    );
  };

  const CONTRIBUTE_MAPPINGS: NavItem[] = useMemo(
    () => [
      {
        text: 'Contributor Dashboard',
        icon: <NoteAlt />,
        path: '/my-reviews',
      },
      {
        text: 'Company Search',
        icon: <Search />,
        path: '/company-search',
      },
      {
        text: 'Open Intelligence Requests',
        icon: <RequestPage />,
        path: '/intelligence-requests',
      },
      {
        text: 'Questions for You',
        icon: <HelpOutline />,
        path: '/network-questions',
        badgeCount: networkQuestionsCount,
      },
    ],
    [networkQuestionsCount],
  );

  const ADMIN_MAPPINGS: NavItem[] = useMemo(
    () => [
      {
        text: 'Admin View',
        icon: <Leaderboard />,
        path: '/admin',
      },
      {
        text: 'Admin Review Feed',
        icon: <RateReview />,
        path: '/admin/reviews',
      },
    ],
    [],
  );

  const LOGIN_MAPPINGS: NavItem[] = useMemo(
    () => [
      {
        text: 'Login',
        icon: <Login />,
        path: '/sign-in',
      },
    ],
    [],
  );

  const isCollapsed = !isOpen && !isMobile;

  // Hide drawer completely on auth-related routes
  if (shouldHideDrawer(location.pathname)) {
    return null;
  }

  const drawerClass = isCollapsed
    ? 'side-drawer side-drawer--collapsed'
    : 'side-drawer side-drawer--expanded';

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      open={isMobile ? isOpen : true}
      onClose={() => setIsOpen(false)}
      className={drawerClass}
    >
      {isCollapsed ? (
        <div className="side-drawer__header side-drawer__header--collapsed">
          <IconButton
            onClick={() => setIsOpen(true)}
            size="small"
            className="side-drawer__toggle"
            aria-label="Expand navigation"
          >
            <ChevronRight />
          </IconButton>
        </div>
      ) : (
        <div className="side-drawer__header">
          <Link to="/my-reviews" className="side-drawer__logo-link">
            <img src={kandirLogo} alt="Kandir" className="side-drawer__logo" />
          </Link>
          <IconButton
            onClick={() => setIsOpen(false)}
            className="side-drawer__toggle"
            aria-label="Collapse navigation"
          >
            <ChevronLeft />
          </IconButton>
        </div>
      )}

      <Divider className="side-drawer__divider" />

      {/* Unauthenticated - Company Search */}
      {(!user || !user?.idToken) && (
        <>
          <List className="side-drawer__list">
            <DrawerNavItem
              item={{
                text: 'Company Search',
                icon: <Search />,
                path: '/company-search',
              }}
              active={isActive('/company-search')}
              collapsed={isCollapsed}
              testId="side-nav-company-search"
            />
          </List>
          <Divider className="side-drawer__divider" />
        </>
      )}

      {/* CONTRIBUTE section - authenticated users */}
      {user && user.idToken && (
        <>
          <List className="side-drawer__list">
            {CONTRIBUTE_MAPPINGS.map(item => (
              <DrawerNavItem
                key={item.path}
                item={item}
                active={isActive(item.path)}
                collapsed={isCollapsed}
                testId={`side-nav${item.path.toLowerCase().replace(/\//g, '-')}`}
              />
            ))}
            {user?.username && (
              <DrawerNavItem
                item={{
                  text: 'My Profile',
                  icon: <AccountCircle />,
                  path: `/user/${user.username}`,
                }}
                active={location.pathname === `/user/${user.username}`}
                collapsed={isCollapsed}
                testId="side-nav-my-profile"
              />
            )}
          </List>
          <Divider className="side-drawer__divider" />
        </>
      )}

      {/* ADMIN section - admins only */}
      {user?.isAdmin && (
        <>
          {!isCollapsed && (
            <div className="side-drawer__section-label">Admin</div>
          )}
          <List className="side-drawer__list">
            {ADMIN_MAPPINGS.map(item => (
              <DrawerNavItem
                key={item.path}
                item={item}
                active={isActive(item.path)}
                collapsed={isCollapsed}
                testId={`side-nav${item.path.toLowerCase().replace(/\//g, '-')}`}
              />
            ))}
          </List>
          <Divider className="side-drawer__divider" />
        </>
      )}

      {/* Login - unauthenticated users */}
      {!user && (
        <List className="side-drawer__list">
          {LOGIN_MAPPINGS.map(item => (
            <DrawerNavItem
              key={item.path}
              item={item}
              active={isActive(item.path)}
              collapsed={isCollapsed}
              testId={`side-nav${item.path.toLowerCase().replace(/\//g, '-')}`}
            />
          ))}
        </List>
      )}
    </Drawer>
  );
};

export default SideDrawer;
